import { 
    PrimitiveCollection, 
    Viewer, 
    ScreenSpaceEventHandler, 
    ScreenSpaceEventType, 
    Math, 
    Cartesian3,
    Cartesian2,
    VerticalOrigin,
    HorizontalOrigin,
    CallbackProperty,
    PointPrimitiveCollection,
    PointPrimitive,
    Color,
    Event
} from "cesium"
import * as turf from '@turf/turf'
import { PolygonPrimitive, LinePrimitive } from '@/lib/extension'
export default class DrawTool {

    /**
     * 
     * @param {Viewer} viewer 
     * @param {*} options 
     */
    constructor(viewer, options) {
        this.viewer = viewer
        this.options = Object.assign({
            showRadius: false,
            unit: 'km'
        }, options)

        this.handler = new ScreenSpaceEventHandler(this.viewer.scene.canvas);

        this.nodes = [] //用于存储临时选中的图形节点
        this.status = 'ready'
        this.moveText = '点击地图开始绘制'
        this.moveLabel = null
        this.closeBill = null // 关闭按钮

        this.events = {
            finish: new Event()
        }
        this.initPrimitives()
    }

    addEvent() {
        // 左键
        this.handler.setInputAction(event => {
            const lnglat = this.getLngLatByPosition(event.position)
            if(!lnglat) {
                return
            }
            const {lng, lat} = lnglat
            if(this.status === 'ready') {
                this.nodes.push([lng, lat])
                //if(this.moveLabel) {
                //    this.viewer.entities.remove(this.moveLabel)
                //}
                this.status = 'drawing'
                this.addNode([lng, lat])
            }else if(this.status === 'drawing') {
                if(this.type === 'circle' && this.nodes.length === 2) {
                    this.nodes.splice(-1, 1, [lng, lat])
                    this.createCircle(this.nodes)
                    this.addNode([lng, lat])
                    this.finish()
                }
            }
            if(this.status === 'finish') {
                const pick = this.viewer.scene.pick(event.position)
                if(pick && pick.id && pick.id.id === 'draw-close') {
                    this.reset()
                }
                return
            }
            
        }, ScreenSpaceEventType.LEFT_CLICK)

        // 鼠标移动
        this.handler.setInputAction(event => {
            const lnglat = this.getLngLatByPosition(event.endPosition)
            if(!lnglat) {
                return
            }
            const {lng, lat} = lnglat
            if(this.status === 'drawing') {
                if(this.type === 'circle') {
                    if(this.nodes.length === 1) {
                        this.nodes.push([lng, lat])
                    }else{
                        this.nodes.splice(-1, 1, [lng, lat])
                    }
                    const text = this.createCircle(this.nodes)
                    if(this.options.showRadius) {
                        this.moveLabel.label.text = text
                        this.moveLabel.position = new CallbackProperty(() => {
                            return Cartesian3.fromDegrees(lng, lat)
                        }, false)
                    }
                }
            }else if(this.status === 'ready') {
                // 跟随鼠标的提示
                if(this.moveLabel) {
                    this.moveLabel.position = new CallbackProperty(() => {
                        return Cartesian3.fromDegrees(lng, lat)
                    }, false)
                }else {
                    this.moveLabel = this.viewer.entities.add({
                        position: Cartesian3.fromDegrees(lng, lat),
                        label: {
                            text: this.moveText,
                            font: '24px sans-serif',
                            scale: 0.5,
                            showBackground: true,
                            backgroundPadding: new Cartesian2(5, 5),
                            verticalOrigin: VerticalOrigin.CENTER,
                            horizontalOrigin: HorizontalOrigin.LEFT,
                            pixelOffset: new Cartesian2(20, 0)
                        }
                    })
                }
            }
        }, ScreenSpaceEventType.MOUSE_MOVE)

        // 右键
        this.handler.setInputAction(event => {
            if(this.type === 'circle') {
                return
            }
        }, ScreenSpaceEventType.RIGHT_CLICK)
    }

    removeEvent() {
        this.handler.removeInputAction(ScreenSpaceEventType.MOUSE_MOVE)
        this.handler.removeInputAction(ScreenSpaceEventType.LEFT_CLICK)
        this.handler.removeInputAction(ScreenSpaceEventType.RIGHT_CLICK)
    }

    initPrimitives() {
        this.primitives = this.viewer.scene.primitives.add(new PrimitiveCollection())
        this.outlinePrimitive = new LinePrimitive({
            outlineColor: 'royalblue',
            outlineWidth: 1,
            outlineOpacity: 0.5
        })
        this.circlePrimitive = new PolygonPrimitive({
            fillColor: 'royalblue',
            outlineColor: '#FF0000',
            fillOpacity: 0.5,
            outlineWidth: 2
        })
        let cp = this.primitives.add(this.circlePrimitive,0)
        this.primitives.add(this.outlinePrimitive,1)
        
        this.nodePoints = this.primitives.add(new PointPrimitiveCollection())
        this.primitives.raiseToTop(this.nodePoints)
        this.viewer.scene.primitives.lowerToBottom(this.primitives)
    }

    draw(type) {
        this.type = type
        this.status = 'ready'
        this.addEvent()
        this.nodes.length = 0
    }

    finish() {
        //this.removeEvent()
        this.addCloseBill()
        this.events.finish.raiseEvent({
            target: {
                type: this.type,
                status: 'finish'
            }
        })
        this.type = ''
        this.status = 'finish'
    }

    addCloseBill() {
        const lastPoint = this.nodes[this.nodes.length-1]
        const center = this.nodes[this.nodes.length-2]
        const bearing = turf.bearing(turf.point(center), turf.point(lastPoint))
        let closeOffset = 15
        if(bearing < 0) {
            closeOffset = -15
        }
        this.closeBill = this.viewer.entities.add({
            id: 'draw-close',
            position: Cartesian3.fromDegrees(...lastPoint, 0),
            label: {
                text: 'x',
                fillColor: Color.RED,
                outlineWidth: 0,
                font: '15px sans-serif',
                horizontalOrigin: HorizontalOrigin.LEFT,
                verticalOrigin: VerticalOrigin.BOTTOM,
                pixelOffset: new Cartesian2(closeOffset, -10)
            },
        })
    }

    reset() {
        this.nodes.length = 0
        this.nodePoints.removeAll()
        this.viewer.entities.remove(this.moveLabel)
        this.moveLabel = null
        this.viewer.entities.remove(this.closeBill)
        this.closeBill = null
        this.circlePrimitive.setPoints([])
        this.outlinePrimitive.setPoints([])
    }

    createCircle(nodes) {
        const start = nodes[0]
        const end = nodes[1]
        const distance = turf.distance(turf.point(start), turf.point(end), {units: 'kilometers'})
        const circle = turf.circle(start, distance, {
            steps: 72,
            units: 'kilometers'
        })
        let content = distance.toFixed(2).concat('km')
        if(this.options.unit === 'nmi') {
            const nmDistance = distance / 1.852
            content = nmDistance.toFixed(2).concat('nm')
        }

        const coordinates = circle.geometry.coordinates[0]
        this.circlePrimitive.setPoints(coordinates)
        this.outlinePrimitive.setPoints(coordinates, start, end)
        return content
    }

    addNode(lnglat) {
        let point = new PointPrimitive()
        point.color = Color.WHITE.withAlpha(0.7)
        point.outlineColor = Color.RED
        point.outlineWidth = 2
        point.position = Cartesian3.fromDegrees(lnglat[0], lnglat[1], 0)
        point.pixelSize = 5
        this.nodePoints.add(point)
    }

    getGeometry() {

    }

    getLngLatByPosition(position) {
        if(position) {
            let cartesian  = this.viewer.camera.pickEllipsoid(
                position,
                this.viewer.scene.globe.ellipsoid
            )
            if(cartesian) {
                let cartographic = this.viewer.scene.globe.ellipsoid.cartesianToCartographic(cartesian)
                return {
                    lng: Math.toDegrees(cartographic.longitude),
                    lat: Math.toDegrees(cartographic.latitude)
                }
            }
        }
        return null
    }

    on(event, callback) {
        if(this.events[event]) {
            this.events[event].addEventListener(event => {
                callback(event)
            })
        }
    }
}